import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { usePageMeta } from '../lib/usePageMeta';

const LAST_UPDATED = '2026-05-12';

const TOC = [
  { id: 'overview', label: '1. Overview and key principles' },
  { id: 'data-we-collect', label: '2. Information we collect' },
  { id: 'how-we-use', label: '3. How we use information' },
  { id: 'ai-training', label: '4. AI training and your content' },
  { id: 'sharing', label: '5. Who we share information with' },
  { id: 'cookies', label: '6. Cookies and tracking' },
  { id: 'retention', label: '7. Data retention' },
  { id: 'rights', label: '8. Your rights and choices' },
  { id: 'children', label: '9. Children’s privacy' },
  { id: 'transfers', label: '10. International data transfers' },
  { id: 'security', label: '11. Security' },
  { id: 'changes', label: '12. Changes to this policy' },
  { id: 'contact', label: '13. Contact' },
];

export function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | StoryMint',
    description:
      'StoryMint Privacy Policy: what data we collect, how we use it, and how to control it. We do not train AI on your content.',
    canonical: 'https://storymint.io/privacy',
    ogType: 'website',
  });

  const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <header className="mb-10">
          <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-3">
            Legal
          </div>
          <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-extrabold text-foreground mb-4 leading-[1.15] tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated:{' '}
            <time dateTime={LAST_UPDATED}>
              {new Date(`${LAST_UPDATED}T00:00:00Z`).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </p>
        </header>

        <nav
          aria-label="Table of contents"
          className="p-6 rounded-xl border border-border bg-card mb-12"
        >
          <h2 className="text-sm font-bold text-foreground tracking-wide uppercase mb-4">
            Contents
          </h2>
          <ol className="space-y-2 text-sm">
            {TOC.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  onClick={(e) => handleTocClick(e, entry.id)}
                  className="text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  {entry.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-10 text-foreground text-[17px] leading-[1.8]">
          <section id="overview" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              1. Overview and key principles
            </h2>
            <p className="text-muted-foreground">
              StoryMint helps authors generate fiction with AI. This policy explains what
              we collect, why, and the controls you have. The short version, in plain
              language:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">We do not train AI on your content.</strong>{' '}
                Your premises, manuscripts, and notes stay yours.
              </li>
              <li>
                <strong className="text-foreground">We collect what we need to run the service</strong>{' '}
                (account info, billing, your generated content, basic usage data), and
                very little else.
              </li>
              <li>
                <strong className="text-foreground">We use a small set of third-party processors</strong>{' '}
                (listed below) and choose them deliberately for privacy posture.
              </li>
              <li>
                <strong className="text-foreground">You can delete your account and data at any time</strong>{' '}
                from your account settings.
              </li>
            </ul>
          </section>

          <section id="data-we-collect" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              2. Information we collect
            </h2>
            <p className="text-muted-foreground">
              We collect four categories of information:
            </p>
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
              Account information
            </h3>
            <p className="text-muted-foreground">
              Email address, display name, password hash (or Google OAuth identifier),
              profile photo URL if you log in through Google. We never see or store your
              raw Google password.
            </p>
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
              Billing information
            </h3>
            <p className="text-muted-foreground">
              When you subscribe to a paid plan, our third-party payment processor
              handles your card details on our behalf. We retain only an opaque customer
              identifier, your subscription status, your plan, your most-recent
              transaction date, and the amount.
            </p>
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
              Content you create
            </h3>
            <p className="text-muted-foreground">
              The premises you submit, the generated manuscripts and covers, your story
              bibles, character profiles, author personas, and any notes you save. This
              content is stored on enterprise cloud infrastructure in the United States,
              encrypted at rest.
            </p>
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
              Usage and technical information
            </h3>
            <p className="text-muted-foreground">
              When you use the service, our servers automatically log: IP address,
              browser type, pages requested, time of request, and any errors that
              occurred. We use this for debugging, capacity planning, and abuse
              prevention. Logs are kept for up to 90 days, then deleted or aggregated
              into anonymous metrics.
            </p>
          </section>

          <section id="how-we-use" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              3. How we use information
            </h2>
            <p className="text-muted-foreground">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide the service you signed up for (generate, store, and let you export your manuscripts)</li>
              <li>Process payments and manage your subscription</li>
              <li>Send transactional emails (account confirmations, billing receipts, password resets)</li>
              <li>Send optional marketing emails (newsletter, product updates) which you can opt out of from any email or in your account settings</li>
              <li>Detect, prevent, and respond to fraud, abuse, and security issues</li>
              <li>Improve the service through aggregated, anonymized usage statistics</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section id="ai-training" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              4. AI training and your content
            </h2>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Your premises, manuscripts, characters,
              and prompts are not used to train any AI model.</strong> Not ours, not our
              providers&apos;.
            </p>
            <p className="text-muted-foreground">
              We send your prompts to third-party model providers over their commercial
              API endpoints. Where available, we use no-retention agreements so the
              model provider does not log or store your content beyond the brief
              transit and inference window. Your content is never opted into any
              provider&apos;s human-review or training pipeline.
            </p>
            <p className="text-muted-foreground">
              If the underlying AI ecosystem changes materially, we will update this
              section before the change goes into effect.
            </p>
          </section>

          <section id="sharing" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              5. Who we share information with
            </h2>
            <p className="text-muted-foreground">
              We share information only with service providers that help us operate the
              service. We never sell your information. Our processors fall into the
              following categories:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Cloud infrastructure providers</strong>{' '}
                that host our application, database, and file storage
              </li>
              <li>
                <strong className="text-foreground">Authentication providers</strong>{' '}
                that handle account sign-in and identity verification
              </li>
              <li>
                <strong className="text-foreground">Payment processors</strong> that
                handle subscription billing, card details, and transaction history
              </li>
              <li>
                <strong className="text-foreground">AI model providers</strong> that
                power text and image generation, under no-retention API agreements
                where available
              </li>
              <li>
                <strong className="text-foreground">Email service providers</strong>{' '}
                that deliver transactional and lifecycle communications
              </li>
              <li>
                <strong className="text-foreground">Content delivery and DNS
                providers</strong> that route web traffic and protect against abuse
              </li>
              <li>
                <strong className="text-foreground">Error monitoring and analytics
                providers</strong> that help us detect issues and understand aggregate
                usage
              </li>
            </ul>
            <p className="text-muted-foreground">
              Each processor is selected for its privacy posture and is bound by
              contractual obligations to use information only as necessary to provide
              the service to us. You can request a current list of processors by emailing
              the privacy address below.
            </p>
            <p className="text-muted-foreground">
              We may also disclose information if required by law, subpoena, or valid
              legal process, or to protect our rights, property, or safety, or that of
              our users.
            </p>
          </section>

          <section id="cookies" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              6. Cookies and tracking
            </h2>
            <p className="text-muted-foreground">
              We use a minimal set of cookies and local-storage entries:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Essential.</strong> Authentication
                tokens issued by Firebase Auth, required to keep you logged in.
              </li>
              <li>
                <strong className="text-foreground">Functional.</strong> UI preferences
                stored in your browser (e.g. light or dark mode).
              </li>
              <li>
                <strong className="text-foreground">Analytics.</strong> If enabled, an
                anonymized usage pixel that tracks aggregate page views and conversion
                events. No personally identifying information is captured.
              </li>
            </ul>
            <p className="text-muted-foreground">
              We do not use third-party advertising cookies or remarketing pixels.
            </p>
          </section>

          <section id="retention" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              7. Data retention
            </h2>
            <p className="text-muted-foreground">
              We retain personal data only as long as we have a reason to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Account and content data: kept while your account is active</li>
              <li>Backups: deleted on a rolling 14-day cycle</li>
              <li>Server logs: deleted or anonymized after 90 days</li>
              <li>Billing records: retained for the period required by applicable tax and accounting laws (typically 7 years)</li>
              <li>Deleted-account data: removed within 30 days of account closure, except where retention is required by law</li>
            </ul>
          </section>

          <section id="rights" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              8. Your rights and choices
            </h2>
            <p className="text-muted-foreground">
              Depending on your jurisdiction (GDPR, UK GDPR, CCPA, and similar), you may
              have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Access</strong> the personal data we hold about you</li>
              <li><strong className="text-foreground">Correct</strong> inaccurate or incomplete data</li>
              <li><strong className="text-foreground">Delete</strong> your data (with limited exceptions for legal or operational needs)</li>
              <li><strong className="text-foreground">Export</strong> your data in a portable format</li>
              <li><strong className="text-foreground">Object</strong> to or restrict certain processing activities</li>
              <li><strong className="text-foreground">Withdraw consent</strong> for marketing emails at any time</li>
              <li><strong className="text-foreground">Lodge a complaint</strong> with your local data-protection authority</li>
            </ul>
            <p className="text-muted-foreground">
              Most of these you can exercise directly in your account settings. For
              anything you can&apos;t do in-app, email{' '}
              <a
                href="mailto:privacy@storymint.io"
                className="text-primary hover:underline"
              >
                privacy@storymint.io
              </a>{' '}
              and we will respond within 30 days.
            </p>
          </section>

          <section id="children" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              9. Children&rsquo;s privacy
            </h2>
            <p className="text-muted-foreground">
              StoryMint is not directed at children under 13, and we do not knowingly
              collect personal information from anyone under 13. If you believe a child
              under 13 has provided us with personal information, contact{' '}
              <a
                href="mailto:privacy@storymint.io"
                className="text-primary hover:underline"
              >
                privacy@storymint.io
              </a>{' '}
              and we will delete it.
            </p>
            <p className="text-muted-foreground">
              In jurisdictions where the digital-consent age is higher (e.g. some EU
              countries set it at 16), the same protection applies up to that local age.
            </p>
          </section>

          <section id="transfers" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              10. International data transfers
            </h2>
            <p className="text-muted-foreground">
              Our service is operated from the United States. If you use the service
              from outside the United States, your information will be transferred to,
              stored, and processed in the United States and other countries where our
              service providers maintain facilities.
            </p>
            <p className="text-muted-foreground">
              Where applicable, we rely on Standard Contractual Clauses, the EU-US Data
              Privacy Framework, or equivalent mechanisms with our processors to
              legitimize international transfers.
            </p>
          </section>

          <section id="security" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              11. Security
            </h2>
            <p className="text-muted-foreground">
              We take security seriously. Concretely:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>All connections are encrypted in transit using TLS 1.2 or higher</li>
              <li>All data is encrypted at rest</li>
              <li>Authentication tokens are short-lived and rotated automatically</li>
              <li>Production secrets are stored in a managed secret store, never in source code</li>
              <li>Access to production infrastructure is restricted to specific employees with two-factor authentication and audited via centralized access logs</li>
              <li>Automated database backups are retained on a rolling schedule</li>
            </ul>
            <p className="text-muted-foreground">
              No system is perfectly secure. If you discover a vulnerability, report it
              to{' '}
              <a
                href="mailto:security@storymint.io"
                className="text-primary hover:underline"
              >
                security@storymint.io
              </a>
              .
            </p>
          </section>

          <section id="changes" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              12. Changes to this policy
            </h2>
            <p className="text-muted-foreground">
              We may update this policy from time to time. Material changes will be
              announced by email to active subscribers and will appear on this page with
              the &ldquo;Last updated&rdquo; date adjusted. Continued use of the service
              after the effective date constitutes acceptance of the updated policy.
            </p>
          </section>

          <section id="contact" style={{ scrollMarginTop: '24px' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 tracking-tight">
              13. Contact
            </h2>
            <p className="text-muted-foreground">
              For privacy questions, data requests, or to report a concern, email{' '}
              <a
                href="mailto:privacy@storymint.io"
                className="text-primary hover:underline"
              >
                privacy@storymint.io
              </a>
              .
            </p>
            <p className="text-muted-foreground">
              For security vulnerability reports, email{' '}
              <a
                href="mailto:security@storymint.io"
                className="text-primary hover:underline"
              >
                security@storymint.io
              </a>
              .
            </p>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
